import { z } from "zod";
import { MAX_FILE_BYTES, MAX_PATH_LENGTH, MAX_TERMINAL_INPUT } from "@racler/shared";

/** Opaque, URL-safe identifiers generated by the agent. */
const opaqueId = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[A-Za-z0-9_-]+$/);
/** A Project: the development unit opened in the browser (a folder inside a Workspace). */
export const projectIdSchema = opaqueId;
/** A Workspace: a locally approved filesystem root that contains Projects. */
export const workspaceIdSchema = opaqueId;

export const terminalIdSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[A-Za-z0-9_-]+$/);

/**
 * Project-relative (or, for Workspace browsing, Workspace-relative) POSIX path ("" = the root of that scope).
 * This is a first, cheap filter (defence in depth). The authoritative check is
 * `resolveProjectPath` in @racler/agent-core, which also handles symlinks.
 */
export const relativePathSchema = z
  .string()
  .max(MAX_PATH_LENGTH)
  .refine((p) => !p.includes("\0"), "path must not contain NUL")
  .refine((p) => !p.startsWith("/") && !p.includes("\\"), "path must be relative and use '/'")
  .refine((p) => !p.split("/").includes(".."), "path must not contain '..'");

/** A Project as the agent reports it to the owner's browser. */
export const projectSchema = z.strictObject({
  id: projectIdSchema,
  name: z.string().min(1).max(200),
  workspaceId: workspaceIdSchema,
  /** Path relative to the Workspace ("" only for a migrated single-folder Workspace). */
  path: relativePathSchema,
  /** Display location, home-relative where possible ("~/Development/app"). Never persisted by the server. */
  location: z.string().max(MAX_PATH_LENGTH),
});
export type Project = z.infer<typeof projectSchema>;

export const dirEntrySchema = z.strictObject({
  name: z.string(),
  kind: z.enum(["file", "directory", "symlink", "other"]),
  size: z.number().int().nonnegative().optional(),
  mtimeMs: z.number().optional(),
});
export type DirEntry = z.infer<typeof dirEntrySchema>;

export const terminalInfoSchema = z.strictObject({
  terminalId: terminalIdSchema,
  projectId: projectIdSchema,
  title: z.string().max(200),
  createdAt: z.number(),
  exited: z.boolean(),
  exitCode: z.number().int().nullable(),
});
export type TerminalInfo = z.infer<typeof terminalInfoSchema>;

export const machineSummarySchema = z.strictObject({
  id: z.uuid(),
  name: z.string(),
  platform: z.string(),
  online: z.boolean(),
  lastSeenAt: z.string().nullable(),
});
export type MachineSummary = z.infer<typeof machineSummarySchema>;

const cols = z.number().int().min(2).max(1000);
const rows = z.number().int().min(1).max(500);

export const fileContentSchema = z.string().max(MAX_FILE_BYTES);
export const terminalInputSchema = z.string().max(MAX_TERMINAL_INPUT);
export const terminalSizeSchema = { cols, rows };

/** A locally approved Workspace: the only place the browser may browse, add or create Projects under. */
export const workspaceSchema = z.strictObject({
  id: workspaceIdSchema,
  name: z.string().min(1).max(200),
  /** Display path, home-relative where possible. The absolute path stays on the machine. */
  path: z.string().max(MAX_PATH_LENGTH),
  /**
   * false for a Workspace that was created by the v1→v2 configuration migration around ONE legacy registered folder
   * (nothing else was approved there). Projects can only be browsed, added or created in browsable Workspaces.
   */
  browsable: z.boolean(),
});
export type Workspace = z.infer<typeof workspaceSchema>;

export const projectNameSchema = z.string().trim().min(1).max(100);

/** Server-side inventory (metadata only, never absolute paths); synced from the agent, see messages.ts. */
export const inventorySchema = z.strictObject({
  workspaces: z.array(z.strictObject({ id: workspaceIdSchema, name: z.string().min(1).max(200) })).max(500),
  projects: z
    .array(
      z.strictObject({
        id: projectIdSchema,
        name: z.string().min(1).max(200),
        workspaceId: workspaceIdSchema,
        path: relativePathSchema,
      }),
    )
    .max(5000),
});
export type Inventory = z.infer<typeof inventorySchema>;
